import type { ReactNode, SVGProps } from 'react';

/**
 * Jeu d'icônes maison « Terrain » (design/components/core/Icon.jsx).
 * Grille 24×24, marge de sécurité 2, trait 1,9 (1,6 en dense < 20px),
 * bouts & angles arrondis, exactement UN accent orange par glyphe.
 *
 * Défauts adaptés au theming V2 : `currentColor` pour l'encre (suit le texte)
 * et `var(--primary)` pour l'accent — vermillon en clair, orange vif en
 * sombre, soit exactement les valeurs du DS dans chaque mode.
 */

type Draw = (ink: string, accent: string, sw: number) => ReactNode;

const GLYPHS = {
  chantier: (k, a, w) => (
    <>
      <path
        d="M3.5 21V10l8.5-6 8.5 6v11"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <rect x="9" y="14" width="6" height="7" rx="1.2" fill={a} />
    </>
  ),
  planning: (k, a, w) => (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" stroke={k} strokeWidth={w} />
      <line x1="3.5" y1="9.5" x2="20.5" y2="9.5" stroke={k} strokeWidth={w} />
      <line x1="8" y1="3" x2="8" y2="6.5" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <line x1="16" y1="3" x2="16" y2="6.5" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <rect x="6.5" y="12.5" width="4" height="4" rx="1" fill={a} />
    </>
  ),
  devis: (k, a, w) => (
    <>
      <path
        d="M6 3.5h7.5L18 8v11.5a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19.5V5A1.5 1.5 0 0 1 6 3.5z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <path d="M13 3.7V8.2h4.6" stroke={k} strokeWidth={w} strokeLinejoin="round" />
      <line x1="7.5" y1="12.5" x2="14.5" y2="12.5" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <rect x="7.5" y="15.5" width="6.5" height="2.6" rx="1.1" fill={a} />
    </>
  ),
  facture: (k, a, w) => (
    <>
      <path
        d="M6 3.5h7.5L18 8v11.5a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19.5V5A1.5 1.5 0 0 1 6 3.5z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <path d="M13 3.7V8.2h4.6" stroke={k} strokeWidth={w} strokeLinejoin="round" />
      <path d="M14.6 12.3a3.1 3.1 0 1 0 0 4.9" stroke={a} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="7.8" y1="13.7" x2="13" y2="13.7" stroke={a} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="7.8" y1="15.7" x2="12.2" y2="15.7" stroke={a} strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  'note-vocale': (k, a, w) => (
    <>
      <rect x="9" y="3.5" width="6" height="10.5" rx="3" stroke={k} strokeWidth={w} />
      <path d="M6 11a6 6 0 0 0 12 0" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <line x1="12" y1="17" x2="12" y2="21" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <rect x="10.5" y="5.5" width="3" height="3" rx="0.9" fill={a} />
    </>
  ),
  arreter: (k, a, w) => (
    <>
      <circle cx="12" cy="12" r="8.5" stroke={k} strokeWidth={w} />
      <rect x="8.6" y="8.6" width="6.8" height="6.8" rx="1.5" fill={a} />
    </>
  ),
  equipes: (k, a, w) => (
    <>
      <circle cx="9" cy="8" r="3.2" stroke={k} strokeWidth={w} />
      <path
        d="M3.5 20c0-3.2 2.5-5.6 5.5-5.6s5.5 2.4 5.5 5.6"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
      />
      <circle cx="17.2" cy="8.6" r="2.4" fill={a} />
      <path d="M15.6 14.3c2.7.2 4.9 2.4 4.9 5.7" stroke={k} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  // Un membre, au singulier : la silhouette d'`equipes` sans sa seconde tête.
  // L'onglet d'affectation choisit UNE personne — deux silhouettes y liraient
  // « l'équipe entière », et l'accent reste sur la tête comme chez `client`.
  membre: (k, a, w) => (
    <>
      <circle cx="12" cy="8" r="3.2" fill={a} />
      <path
        d="M6 20c0-3.3 2.7-6 6-6s6 2.7 6 6"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
      />
    </>
  ),
  conges: (k, a, w) => (
    <>
      <rect x="3.5" y="8" width="17" height="12" rx="2.5" stroke={k} strokeWidth={w} />
      <path
        d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="10.5" y="11" width="3" height="6" rx="1" fill={a} />
    </>
  ),
  pointage: (k, a, w) => (
    <>
      <circle cx="12" cy="12" r="8.5" stroke={k} strokeWidth={w} />
      <path
        d="M12 7.5V12l3.2 1.9"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="1.5" fill={a} />
    </>
  ),
  // B1 : deux entrées qui ne se valent pas — photographier maintenant, ou
  // reprendre une photo déjà prise. Deux gestes distincts, donc deux glyphes :
  // `photos` est un paysage encadré, il nomme la galerie, pas l'action.
  appareil: (k, a, w) => (
    <>
      <path
        d="M4 8.5h3.2l1.5-2.4h6.6l1.5 2.4H20a1.6 1.6 0 0 1 1.6 1.6v7.8A1.6 1.6 0 0 1 20 19.5H4a1.6 1.6 0 0 1-1.6-1.6v-7.8A1.6 1.6 0 0 1 4 8.5z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <circle cx="12" cy="14" r="3.4" stroke={k} strokeWidth={w} />
      <circle cx="12" cy="14" r="1.5" fill={a} />
    </>
  ),
  pellicule: (k, a, w) => (
    <>
      <rect x="7" y="3.5" width="13.5" height="10" rx="2" stroke={k} strokeWidth={w} />
      <path
        d="M4.5 7v12a1.6 1.6 0 0 0 1.6 1.6h11.4"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="10" y="6.4" width="3.4" height="3.4" rx="1" fill={a} />
    </>
  ),
  photos: (k, a, w) => (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke={k} strokeWidth={w} />
      <circle cx="8.5" cy="10" r="1.9" fill={a} />
      <path
        d="M4 17.5 9.5 12.5 13 15.5 16.5 12 20 15.5"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  recherche: (k, a, w) => (
    <>
      <circle cx="10.5" cy="10.5" r="6.2" stroke={k} strokeWidth={w} />
      <line x1="15.3" y1="15.3" x2="20" y2="20" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <circle cx="10.5" cy="10.5" r="2.3" fill={a} />
    </>
  ),
  reglages: (k, a, w) => (
    <>
      <line x1="4" y1="7" x2="20" y2="7" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <line x1="4" y1="17" x2="20" y2="17" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <circle cx="15" cy="7" r="2.5" fill={a} />
      <circle cx="9" cy="12" r="2.5" fill={a} />
      <circle cx="16" cy="17" r="2.5" fill={a} />
    </>
  ),
  alerte: (k, a, w) => (
    <>
      <path d="M12 4.2 20.5 19.5H3.5z" stroke={k} strokeWidth={w} strokeLinejoin="round" />
      <line x1="12" y1="10" x2="12" y2="14" stroke={a} strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.6" r="1.1" fill={a} />
    </>
  ),
  notif: (k, a, w) => (
    <>
      <path
        d="M6 10a6 6 0 0 1 12 0c0 4.5 1.8 5.5 1.8 5.5H4.2S6 14.5 6 10z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <path d="M10 19a2 2 0 0 0 4 0" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <circle cx="17.5" cy="6.5" r="2.6" fill={a} />
    </>
  ),
  materiaux: (k, a, w) => (
    <>
      <rect x="3" y="12.5" width="8" height="6" rx="1" stroke={k} strokeWidth={w} />
      <rect x="13" y="12.5" width="8" height="6" rx="1" stroke={k} strokeWidth={w} />
      <rect x="8" y="6" width="8" height="6" rx="1" fill={a} />
    </>
  ),
  fermer: (k, _a, w) => (
    <>
      <line x1="6.5" y1="6.5" x2="17.5" y2="17.5" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <line x1="17.5" y1="6.5" x2="6.5" y2="17.5" stroke={k} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  oeil: (k, a, w) => (
    <>
      <path
        d="M2.8 12C5 7.6 8.2 5.8 12 5.8s7 1.8 9.2 6.2C19 16.4 15.8 18.2 12 18.2S5 16.4 2.8 12z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.6" fill={a} />
    </>
  ),
  'oeil-barre': (k, a, w) => (
    <>
      <path
        d="M2.8 12C5 7.6 8.2 5.8 12 5.8s7 1.8 9.2 6.2C19 16.4 15.8 18.2 12 18.2S5 16.4 2.8 12z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.6" fill={a} />
      <line x1="5" y1="4.8" x2="19" y2="19.2" stroke={k} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  valider: (_k, a) => (
    <path
      d="M5 12.5 10 17.5 19 7"
      stroke={a}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  menu: (k, a, w) => (
    <>
      <line x1="4" y1="7" x2="20" y2="7" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke={a} strokeWidth={w} strokeLinecap="round" />
      <line x1="4" y1="17" x2="20" y2="17" stroke={k} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  // Poignée de saisie (FR-09) : deux colonnes de trois points. La rangée
  // MÉDIANE porte l'accent — même lecture que `menu`, dont seule la ligne du
  // milieu est orange. Des points et non des lignes : la poignée se prend,
  // elle n'ouvre rien.
  grip: (k, a) => (
    <>
      <circle cx="9" cy="6.5" r="1.75" fill={k} />
      <circle cx="15" cy="6.5" r="1.75" fill={k} />
      <circle cx="9" cy="12" r="1.75" fill={a} />
      <circle cx="15" cy="12" r="1.75" fill={a} />
      <circle cx="9" cy="17.5" r="1.75" fill={k} />
      <circle cx="15" cy="17.5" r="1.75" fill={k} />
    </>
  ),
  retirer: (k, a, w) => (
    <>
      <circle cx="10" cy="8" r="3.2" stroke={k} strokeWidth={w} />
      <path
        d="M4 20c0-3.2 2.7-5.6 6-5.6 1 0 1.9.2 2.7.6"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
      />
      <line x1="15" y1="18" x2="21" y2="18" stroke={a} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  ajouter: (k, a, w) => (
    <>
      <circle cx="10" cy="8" r="3.2" stroke={k} strokeWidth={w} />
      <path
        d="M4 20c0-3.2 2.7-5.6 6-5.6 1 0 1.9.2 2.7.6"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
      />
      <line x1="18" y1="15" x2="18" y2="21" stroke={a} strokeWidth={w} strokeLinecap="round" />
      <line x1="15" y1="18" x2="21" y2="18" stroke={a} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  creer: (_k, a) => (
    <>
      <line x1="12" y1="5" x2="12" y2="19" stroke={a} strokeWidth="2.2" strokeLinecap="round" />
      <line x1="5" y1="12" x2="19" y2="12" stroke={a} strokeWidth="2.2" strokeLinecap="round" />
    </>
  ),
  envoyer: (k, a, w) => (
    <>
      <path
        d="M21 3.5 2.5 11 10 13.5 12.5 20.5z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <line x1="21" y1="3.5" x2="10" y2="13.5" stroke={a} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  inviter: (k, a, w) => (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2.5" stroke={k} strokeWidth={w} />
      <path
        d="M3.8 7.2 12 12.8 20.2 7.2"
        stroke={a}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  dashboard: (k, a, w) => (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" stroke={k} strokeWidth={w} />
      <rect x="14" y="3" width="7" height="7" rx="1.5" fill={a} />
      <rect x="3" y="14" width="7" height="7" rx="1.5" stroke={k} strokeWidth={w} />
      <rect x="14" y="14" width="7" height="7" rx="1.5" stroke={k} strokeWidth={w} />
    </>
  ),
  modifier: (k, a, w) => (
    <>
      <path
        d="M4.5 19.5l1.1-3.9L16.7 4.5a2 2 0 0 1 2.8 2.8L8.4 18.4l-3.9 1.1z"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line x1="14" y1="7.2" x2="16.8" y2="10" stroke={a} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  // Fiche client : une carte de contact — une personne et ses coordonnées.
  // Ni l'immeuble de `batiment` (une entreprise externe), ni les deux
  // silhouettes d'`equipes` (les gens de la maison). L'accent est sur la
  // tête : ce que le répertoire contient, ce sont des interlocuteurs.
  client: (k, a, w) => (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke={k} strokeWidth={w} />
      <circle cx="9" cy="10.6" r="2.1" fill={a} />
      <path
        d="M5.9 16.1c0-1.8 1.4-3.1 3.1-3.1s3.1 1.3 3.1 3.1"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
      />
      <line x1="14.8" y1="10.2" x2="18" y2="10.2" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <line x1="14.8" y1="13.8" x2="17" y2="13.8" stroke={k} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  // Glyphes ABSENTS du jeu compilé, dessinés aux règles maison (grille 24,
  // trait 1,9, bouts arrondis, UN seul accent orange) — à remonter au DS.
  batiment: (k, a, w) => (
    <>
      <path
        d="M4 20V6.5a1.5 1.5 0 0 1 1.5-1.5h7A1.5 1.5 0 0 1 14 6.5V20"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 20v-9h4.5A1.5 1.5 0 0 1 20 12.5V20"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line x1="3" y1="20" x2="21" y2="20" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <rect x="7" y="8" width="4" height="3.5" rx="0.8" fill={a} />
    </>
  ),
  localisation: (k, a, w) => (
    <>
      <path
        d="M12 21s6.5-5.6 6.5-10.2A6.5 6.5 0 0 0 5.5 10.8C5.5 15.4 12 21 12 21z"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10.5" r="2.4" fill={a} />
    </>
  ),
  archiver: (k, a, w) => (
    <>
      <rect x="3" y="4.5" width="18" height="4" rx="1.2" stroke={k} strokeWidth={w} />
      <path
        d="M5 8.5v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
      />
      <line x1="10" y1="12.5" x2="14" y2="12.5" stroke={a} strokeWidth={w} strokeLinecap="round" />
    </>
  ),
  desarchiver: (k, a, w) => (
    <>
      <rect x="3" y="4.5" width="18" height="4" rx="1.2" stroke={k} strokeWidth={w} />
      <path
        d="M5 8.5v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
      />
      <path
        d="M12 16.5v-4.6M9.9 13.7 12 11.6l2.1 2.1"
        stroke={a}
        strokeWidth={w}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  // Proposer : le contenu SORT du bac vers le haut — il part à la décision
  // d'un autre, il n'est pas envoyé au destinataire final (accent = ce qui
  // monte).
  proposer: (k, a, w) => (
    <>
      <path
        d="M4.5 13.5v5a1.6 1.6 0 0 0 1.6 1.6h11.8a1.6 1.6 0 0 0 1.6-1.6v-5"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 15.2V4.2M8.3 7.9 12 4.2l3.7 3.7"
        stroke={a}
        strokeWidth={w}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  // Reprendre : l'exact miroir de `proposer` — ce qui était sorti redescend
  // dans le bac. Retirer sa proposition n'est pas la refuser.
  reprendre: (k, a, w) => (
    <>
      <path
        d="M4.5 13.5v5a1.6 1.6 0 0 0 1.6 1.6h11.8a1.6 1.6 0 0 0 1.6-1.6v-5"
        stroke={k}
        strokeWidth={w}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 4.2v11M8.3 11.5 12 15.2l3.7-3.7"
        stroke={a}
        strokeWidth={w}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  // Relancer : la flèche repart d'où elle vient — le même vocal, de nouveau
  // dans la file (VO-23).
  relancer: (k, a, w) => (
    <>
      <path
        d="M19 12a7 7 0 1 1-2.05-4.95"
        stroke={k}
        strokeWidth={w}
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M17 3.3v3.8h-3.8"
        stroke={a}
        strokeWidth={w}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  poubelle: (k, a, w) => (
    <>
      <line x1="4.5" y1="7" x2="19.5" y2="7" stroke={k} strokeWidth={w} strokeLinecap="round" />
      <path
        d="M6.6 7l.85 12.05A1.6 1.6 0 0 0 9.05 20.5h5.9a1.6 1.6 0 0 0 1.6-1.45L17.4 7"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <rect x="9.3" y="3.5" width="5.4" height="2.6" rx="1.2" fill={a} />
    </>
  ),
  filtres: (k, a, w) => (
    <>
      <path
        d="M3.8 5.5h16.4l-6.3 7.2h-3.8L3.8 5.5z"
        stroke={k}
        strokeWidth={w}
        strokeLinejoin="round"
      />
      <rect x="10.3" y="13.2" width="3.4" height="6.3" rx="1.5" fill={a} />
    </>
  ),
  'chevron-up': (k, _a, w) => (
    <polyline
      points="6 15 12 9 18 15"
      stroke={k}
      strokeWidth={w}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  'chevron-down': (k, _a, w) => (
    <polyline
      points="6 9 12 15 18 9"
      stroke={k}
      strokeWidth={w}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  'chevron-left': (k, _a, w) => (
    <polyline
      points="15 6 9 12 15 18"
      stroke={k}
      strokeWidth={w}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  'chevron-right': (k, _a, w) => (
    <polyline
      points="9 6 15 12 9 18"
      stroke={k}
      strokeWidth={w}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
} satisfies Record<string, Draw>;

export type IconName = keyof typeof GLYPHS;

export const ICON_NAMES = Object.keys(GLYPHS) as Array<IconName>;

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'color'> {
  name: IconName;
  size?: number;
  color?: string;
  accent?: string;
  title?: string;
}

function Icon({
  name,
  size = 24,
  color = 'currentColor',
  accent = 'var(--primary)',
  title,
  className,
  ...props
}: IconProps) {
  const draw = GLYPHS[name];
  const sw = size < 20 ? 1.6 : 1.9;
  return (
    <svg
      data-slot="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={className}
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {draw(color, accent, sw)}
    </svg>
  );
}

export { Icon };
